// One buyer's return request on the seller's order page, read as a
// conversation: the items asked back first (what, which size, how many,
// for how much), then the thread — the buyer's message on the left (the
// reason and what they wrote), the seller's answer on the right, and what
// happened in between (cancelled, goods received) as small lines between
// them — and under it what the seller can do now.
import React, { useState } from "react"
import { ChevronDownIcon, ClockIcon } from "@heroicons/react/24/outline"
import Button from "aldoo-ra/Button"
import { describeLine } from "aldoo-ra/Store/lines"
import { Chip, Thumb, money, dateTime } from "./ui"
import { REASON_LABELS } from "./ReturnDialog"
import { BuyerAvatar, SellerAvatar } from "./Avatars"

// a request's state, as the seller reads it
export const REQUEST_LABELS = { open: "waiting for your answer", accepted: "waiting for items", returned: "item returned", declined: "declined", canceled: "canceled by the buyer" }
export const REQUEST_TONES = {
  open: "bg-admin_warning/10 text-admin_warning",
  // waiting for the items: something still to happen — the theme's warning tint, dark text
  // (the waiting chip under the answer shares it)
  accepted: "bg-admin_warning/20 text-admin_text",
  returned: "bg-[#6b5ca5]/10 text-[#6b5ca5]",
  declined: "bg-admin_error/10 text-admin_error",
  canceled: "bg-admin_neutral_1 text-admin_text_secondary",
}

/** A line between the messages: something that happened, and when. */
const Event = ({ children, at, locale, t, tone = "text-black/45" }) => (
  <div className={`flex items-center gap-3 text-[11px] uppercase tracking-wide ${tone}`}>
    <span className="flex-1 h-px bg-black/10" />
    <span>
      {children}
      {at ? ` · ${dateTime(at, locale, t)}` : ""}
    </span>
    <span className="flex-1 h-px bg-black/10" />
  </div>
)

/** A message of the thread: who, when, and what — the buyer's on the left, the seller's on the right. */
const Message = ({ side, who, avatar, at, locale, t, tone, children, testid }) => {
  const mine = side === "right"
  return (
    <div className={`flex items-end gap-2 ${mine ? "flex-row-reverse" : ""}`} data-testid={testid}>
      {avatar}
      <div className={`flex flex-col max-w-[85%] md:max-w-[75%] ${mine ? "items-end" : "items-start"}`}>
        <span className="text-[11px] text-black/45 mb-1 px-1">
          {who}
          {at ? ` · ${dateTime(at, locale, t)}` : ""}
        </span>
        <div className={`rounded-2xl px-4 py-2.5 text-sm whitespace-pre-line break-words ${mine ? "rounded-br-sm" : "rounded-bl-sm"} ${tone}`}>{children}</div>
      </div>
    </div>
  )
}

/**
 * `request` (newest first in the list, so `number` counts from the oldest),
 * `cartLineOf(line)` the order line a request line names (its picture, what
 * was chosen on it, its price), `buyer` the name the buyer goes by,
 * `onAnswer(request, decision)`. Goods of an accepted request are taken in with
 * the order's Returned action, which starts from that request.
 */
export default function RequestThread({ request, number, order, cartLineOf, buyer, buyerPerson = null, seller = null, t, locale, busy, onAnswer, defaultOpen = true }) {
  const units = (request.lines || []).reduce((sum, line) => sum + (line.quantity || 0), 0)
  const answered = request.status === "accepted" || request.status === "declined"
  const stage = request.status === "accepted" && request.returnedAt ? "returned" : request.status
  // folded to its header: the request's number, date and state are enough once it is settled
  const [open, setOpen] = useState(defaultOpen)
  return (
    <article className="rounded-lg border border-black/10 overflow-hidden text-sm" data-testid="order-request">
      {/* which request, when, where it stands */}
      <header
        role="button"
        tabIndex={0}
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), setOpen((value) => !value))}
        className={`flex flex-wrap items-center gap-2 px-4 py-2.5 bg-black/[0.03] cursor-pointer select-none hover:bg-black/[0.05] ${open ? "border-b border-black/10" : ""}`}
        data-testid="order-request-toggle"
      >
        <span className="font-semibold">{t("Return request #%s", number)}</span>
        <span className="text-black/50">{request.at ? dateTime(request.at, locale, t) : ""}</span>
        <span className="flex-1" />
        {request.late && <Chip className="bg-admin_warning/10 text-admin_warning">{t`after the return period`}</Chip>}
        {/* accepted and its goods back: the request is done — it says so */}
        <Chip className={REQUEST_TONES[stage] || ""}>{t(REQUEST_LABELS[stage] || stage)}</Chip>
        <ChevronDownIcon className={`w-5 h-5 shrink-0 text-black/50 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden />
      </header>

      {open && (
      <>

      {/* what the buyer wants to send back */}
      <section className="px-4 py-3 border-b border-black/10">
        <div className="text-[11px] uppercase tracking-wide text-black/50 mb-2">{t("Items to return (%s)", units)}</div>
        <div className="flex flex-col gap-2">
          {(request.lines || []).map((line, at) => {
            const item = cartLineOf(line)
            return (
              <div key={at} className="flex items-center gap-3" data-testid="order-request-line">
                <Thumb src={item.thumbnail} name={line.name || line.pid} className="w-[44px] h-[44px] rounded shrink-0" />
                <div className="flex-1 min-w-0">
                  <div className="font-medium break-words">{describeLine({ ...item, ...line }, line.name || item.name || line.pid)}</div>
                  {line.sku && <div className="text-xs text-black/50">{line.sku}</div>}
                </div>
                <div className="shrink-0 text-right tabular-nums">
                  <div className="font-medium">× {line.quantity}</div>
                  {item.price !== undefined && item.price !== null && <div className="text-xs text-black/50">{money(item.price * (line.quantity || 1), order.currency)}</div>}
                </div>
              </div>
            )
          })}
        </div>
      </section>

      {/* the conversation: the buyer asks, the seller answers, and what happened between */}
      <section className="px-4 py-4 flex flex-col gap-4 bg-white">
        <Message t={t} side="left" who={buyer || t`Buyer`} avatar={<BuyerAvatar person={buyerPerson} />} at={request.at} locale={locale} tone="bg-black/[0.05] text-black" testid="order-request-buyer">
          <div className="text-[11px] uppercase tracking-wide text-black/50 mb-0.5">{t`Reason`}</div>
          <div className="font-semibold" data-testid="order-request-reason">{t(REASON_LABELS[request.reason] || request.reason)}</div>
          <div className={`mt-1.5 ${request.note ? "" : "italic text-black/45"}`}>{request.note || t`No note`}</div>
        </Message>

        {request.status === "canceled" && <Event t={t} at={request.canceledAt} locale={locale}>{t`Canceled by the buyer`}</Event>}

        {answered && (
          <Message t={t}
            side="right"
            who={t`You`}
            avatar={<SellerAvatar seller={seller} />}
            at={request.resolvedAt}
            locale={locale}
            tone={request.status === "accepted" ? "bg-admin_success/10 text-black" : "bg-admin_error/10 text-black"}
            testid="order-request-answer"
          >
            <div className={`font-semibold mb-0.5 ${request.status === "accepted" ? "text-admin_success" : "text-admin_error"}`}>
              {request.status === "accepted" ? t`You accepted the request` : t`You declined the request`}
            </div>
            {request.message || "—"}
          </Message>
        )}

        {request.status === "open" && <div className="text-right text-xs italic text-black/45">{t`Waiting for your answer…`}</div>}

        {/* accepted is not arrived: the goods are still to come back, and are taken in with the order's Returned action */}
        {request.status === "accepted" && !request.returnedAt && (
          <div className="flex justify-center" data-testid="order-request-awaiting">
            {/* the same colour as the request's "waiting for items" state it explains */}
            <span className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium text-center ${REQUEST_TONES.accepted}`}>
              <ClockIcon className="w-4 h-4 shrink-0" aria-hidden />
              {t`Waiting for the goods — when they arrive, use Order actions → Returned`}
            </span>
          </div>
        )}

        {request.returnedAt && (
          <Event t={t} at={request.returnedAt} locale={locale} tone="text-admin_success">
            {t`Goods received`}
          </Event>
        )}
      </section>

      {/* what the seller can do now */}
      {request.status === "open" && (
        <footer className="flex flex-wrap justify-end gap-2 px-4 py-3 border-t border-black/10 bg-black/[0.02]">
          <Button variant="admin_secondary" disabled={busy} onClick={() => onAnswer(request, "declined")} data-testid="request-decline">
            {t`Decline`}
          </Button>
          <Button variant="admin_primary" disabled={busy} onClick={() => onAnswer(request, "accepted")} data-testid="request-accept">
            {t`Accept`}
          </Button>
        </footer>
      )}
      </>
      )}
    </article>
  )
}
